import { useState, useCallback, useEffect, useRef } from 'react';
import { IconSparkles, IconChevronLeft, IconChevronRight, IconExternalLink, IconRefresh } from '@tabler/icons-react';
import { Check, Circle, Loader2 } from 'lucide-react';
import {
  generateOutline,
  refineOutline,
  generateSlidesFastestFirst,
  SlideOutline,
  SlideContent,
} from '../services/explainerService';
import { SlideWithImage } from './slidedeck/SlideWithImage';
import { updateJournalBlock } from '../../../services/journalBlockService';
import { useJournalSummaryUpdates } from '../../../hooks/useJournalSummaryUpdates';

type BlockState = 'config' | 'outline' | 'generating' | 'ready';

// Content interface for state persistence
export interface SlideDeckBlockContent {
  generating?: boolean;  // AI-triggered generation in progress
  state: BlockState;
  topic: string;
  duration: number;
  outline: SlideOutline | null;
  generatedSlides: SlideContent[];
  currentSlideIndex: number;
}

interface SlideDeckBlockProps {
  initialState?: SlideDeckBlockContent;
  onStateChange?: (state: SlideDeckBlockContent) => void;
  onOpenInTab?: (slides: SlideContent[], title: string) => void;
  onTitleChange?: (title: string) => void;  // Callback to update block title
  journalId?: string;  // For context-grounded generation
  blockId?: string;    // For block-level persistence
}

const DURATION_OPTIONS = [
  { label: '5 min', value: 5 },
  { label: '10 min', value: 10 },
  { label: '15 min', value: 15 },
  { label: '20 min', value: 20 },
];

export const SlideDeckBlock = ({ initialState, onStateChange, onOpenInTab, onTitleChange, journalId, blockId }: SlideDeckBlockProps) => {
  // Hooks
  const { triggerUpdate } = useJournalSummaryUpdates(journalId || '');

  // State machine - restore from initialState if available
  const [state, setState] = useState<BlockState>(initialState?.state || 'config');

  // Config state
  const [topic, setTopic] = useState(initialState?.topic || '');
  const [duration, setDuration] = useState(initialState?.duration || 10);

  // Outline state
  const [outline, setOutline] = useState<SlideOutline | null>(initialState?.outline || null);
  const [refinementInput, setRefinementInput] = useState('');
  const [isGeneratingOutline, setIsGeneratingOutline] = useState(false);

  // Generating state
  const [generatedSlides, setGeneratedSlides] = useState<SlideContent[]>(initialState?.generatedSlides || []);
  const [currentGeneratingIndex, setCurrentGeneratingIndex] = useState(0);
  const [isGeneratingSlides, setIsGeneratingSlides] = useState(false); // Track if actively generating

  // Ready state
  const [currentSlideIndex, setCurrentSlideIndex] = useState(initialState?.currentSlideIndex || 0);

  // Track previous state to avoid infinite loops - initialize with initial state
  const previousStateRef = useRef<string>(
    JSON.stringify({
      state: initialState?.state || 'config',
      topic: initialState?.topic || '',
      duration: initialState?.duration || 10,
      outline: initialState?.outline || null,
      generatedSlides: initialState?.generatedSlides || [],
      currentSlideIndex: initialState?.currentSlideIndex || 0,
    })
  );
  const onStateChangeRef = useRef(onStateChange);

  // Keep onStateChange ref updated
  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  // Notify parent of state changes for persistence - only when actual data changes
  useEffect(() => {
    const currentState: SlideDeckBlockContent = {
      state,
      topic,
      duration,
      outline,
      generatedSlides,
      currentSlideIndex,
    };

    const currentStateJson = JSON.stringify(currentState);

    // Only notify if state actually changed (skip initial mount since parent already has this)
    if (previousStateRef.current !== currentStateJson) {
      previousStateRef.current = currentStateJson;
      onStateChangeRef.current?.(currentState);
    }
  }, [state, topic, duration, outline, generatedSlides, currentSlideIndex]);

  // Track if we've triggered auto-generation to prevent duplicates
  const hasTriggeredAutoGenerationRef = useRef(false);

  // Auto-generate for AI-triggered blocks (generating: true with no slides)
  // This handles the case where Live agent adds a block that needs generation
  useEffect(() => {
    // Only trigger once on mount, and only if AI-triggered (generating flag + no slides)
    if (
      initialState?.generating === true &&
      initialState?.state === 'generating' &&
      initialState?.generatedSlides?.length === 0 &&
      initialState?.topic &&
      !hasTriggeredAutoGenerationRef.current
    ) {
      hasTriggeredAutoGenerationRef.current = true;
      console.log('[SlideDeck] AI-triggered generation detected, starting auto-generation...');

      // Start the generation process
      (async () => {
        setIsGeneratingOutline(true);
        try {
          // Step 1: Generate outline
          const outlineResult = await generateOutline(
            initialState.topic,
            initialState.duration || 10,
            journalId
          );
          setOutline(outlineResult);

          // Update title
          if (outlineResult.title) {
            onTitleChange?.(outlineResult.title);
          }

          // Step 2: Create skeleton slides and start generation immediately
          const skeletonSlides: SlideContent[] = outlineResult.slides.map((item, i) => {
            if (i === 0) {
              // Title slide is instant
              return {
                id: item.id,
                order: i + 1,
                title: outlineResult.title,
                layout: 'title' as const,
                status: 'ready' as const,
                content: {
                  heading: outlineResult.title,
                  subheading: outlineResult.description,
                },
              };
            }
            // Other slides start as skeletons
            return {
              id: item.id,
              order: i + 1,
              title: item.title,
              layout: 'text-only' as const,
              status: 'generating' as const,
              content: {
                heading: item.title,
                body: item.description,
              },
            };
          });

          setGeneratedSlides(skeletonSlides);
          setState('ready');
          setCurrentSlideIndex(0);
          setIsGeneratingSlides(true);
          setIsGeneratingOutline(false);

          // Trigger summary update
          if (journalId && blockId) {
            triggerUpdate('explainer', 'add', {
              blockId,
              isAIGenerated: true,
              hasContent: true,
              forceImmediate: true
            });
          }

          // Step 3: Generate remaining slides in parallel
          for await (const { slide, index } of generateSlidesFastestFirst(outlineResult, journalId, 5)) {
            if (index === 0) continue; // Skip title slide

            setGeneratedSlides(prev => {
              const updated = [...prev];
              updated[index] = { ...slide, status: 'ready' as const };
              return updated;
            });
            setCurrentGeneratingIndex(prev => prev + 1);
          }

          setIsGeneratingSlides(false);

        } catch (error) {
          console.error('[SlideDeck] Auto-generation failed:', error);
          setIsGeneratingOutline(false);
          setState('config'); // Fall back to config on error
        }
      })();
    } else if (initialState?.state === 'generating' && !initialState?.generating) {
      // Recovery: If we mount with 'generating' state but no AI flag (e.g. page refreshed mid-generation)
      console.log('[SlideDeck] Recovering from stuck generating state - switching to ready');
      setState('ready');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Auto-switch to ready state when first slide is generated
  // This gives users immediate access to view slides while others generate
  useEffect(() => {
    if (state === 'generating' && generatedSlides.length === 1) {
      setState('ready');
      setCurrentSlideIndex(0);
    }
  }, [state, generatedSlides.length]);

  // Generate outline
  const handleGenerateOutline = useCallback(async () => {
    if (!topic.trim()) return;

    setIsGeneratingOutline(true);
    try {
      const result = await generateOutline(topic, duration, journalId);
      setOutline(result);
      setState('outline');
    } catch (error) {
      console.error('Failed to generate outline:', error);
    } finally {
      setIsGeneratingOutline(false);
    }
  }, [topic, duration, journalId]);

  // Regenerate outline with refinement - uses AI to interpret feedback
  const handleRegenerateOutline = useCallback(async () => {
    if (!refinementInput.trim() || !outline) return;

    setIsGeneratingOutline(true);
    try {
      // Use refineOutline for intelligent interpretation of feedback
      const result = await refineOutline(topic, outline, refinementInput, journalId);
      setOutline(result);
      setRefinementInput('');
    } catch (error) {
      console.error('Failed to refine outline:', error);
    } finally {
      setIsGeneratingOutline(false);
    }
  }, [topic, outline, refinementInput, journalId]);

  // Generate slides with skeleton-first approach for instant feedback
  const handleGenerateSlides = useCallback(async () => {
    if (!outline) return;

    // Update block title immediately
    if (outline.title) {
      onTitleChange?.(outline.title);
    }

    // Step 1: Create skeleton slides IMMEDIATELY (before any API calls)
    const skeletonSlides: SlideContent[] = outline.slides.map((item, i) => {
      if (i === 0) {
        // Title slide is instant - no API call needed!
        // No image - uses gradient background (avoids unreliable Wikimedia searches)
        return {
          id: item.id,
          order: i + 1,
          title: outline.title,
          layout: 'title' as const,
          status: 'ready' as const,
          content: {
            heading: outline.title,
            subheading: outline.description,
          },
        };
      }
      // All other slides start as skeletons
      return {
        id: item.id,
        order: i + 1,
        title: item.title,
        layout: 'text-only' as const, // Placeholder layout
        status: 'generating' as const,
        content: {
          heading: item.title,
          body: item.description,
        },
      };
    });

    // Step 2: Show slides IMMEDIATELY (user sees skeletons right away!)
    setGeneratedSlides(skeletonSlides);
    setState('ready'); // Go to ready state immediately!
    setCurrentSlideIndex(0);
    setIsGeneratingSlides(true);
    setCurrentGeneratingIndex(1); // First slide (title) is already done

    // Trigger summary update in background (non-blocking)
    if (journalId && blockId) {
      triggerUpdate('explainer', 'add', {
        blockId,
        isAIGenerated: true,
        hasContent: true,
        forceImmediate: true
      });
    }

    // Step 3: Generate remaining slides in background (skip index 0 = title)
    try {
      for await (const { slide, index } of generateSlidesFastestFirst(outline, journalId, 5)) {
        // Skip title slide (already done)
        if (index === 0) continue;

        // Update skeleton with real content
        setGeneratedSlides(prev => {
          const updated = [...prev];
          updated[index] = { ...slide, status: 'ready' as const };
          return updated;
        });
        setCurrentGeneratingIndex(prev => prev + 1);
      }
    } catch (error) {
      console.error('Parallel slide generation error:', error);
    }

    setIsGeneratingSlides(false);

    // Save block metadata after generation completes
    if (blockId && outline && outline.slides.length > 0) {
      // Run in background - don't block
      updateJournalBlock(blockId, {
        summary: outline.description || `Explainer on ${topic}`,
        tutor_context: `Explainer on ${topic} with ${outline.slideCount} slides. Key points: ${outline.slides.map(s => s.title).join(', ')}`,
        key_points: outline.slides.map(s => s.title),
      }).catch(error => console.error('Failed to update block metadata:', error));

      // Trigger final summary update in background
      if (journalId) {
        triggerUpdate('explainer', 'add', {
          blockId,
          isAIGenerated: true,
          hasContent: true,
          forceImmediate: true
        });
      }
    }
  }, [outline, journalId, blockId, topic, triggerUpdate, onTitleChange]);

  // Navigation
  const handlePrevSlide = useCallback(() => {
    setCurrentSlideIndex(prev => Math.max(0, prev - 1));
  }, []);

  const handleNextSlide = useCallback(() => {
    setCurrentSlideIndex(prev => Math.min(generatedSlides.length - 1, prev + 1));
  }, [generatedSlides.length]);

  // Open in tab
  const handleOpenInTab = useCallback(() => {
    if (onOpenInTab && generatedSlides.length > 0 && outline) {
      onOpenInTab(generatedSlides, outline.title);
    }
  }, [onOpenInTab, generatedSlides, outline]);

  // Handle when an image is resolved for a slide - update the slide in state (for persistence)
  const handleImageResolved = useCallback((slideId: string, imageUrl: string) => {
    setGeneratedSlides(prev => {
      const slideIndex = prev.findIndex(s => s.id === slideId);
      if (slideIndex === -1) return prev;

      const slide = prev[slideIndex];
      if (!slide.content?.image) return prev;

      // Update the slide with the resolved image URL
      const updatedSlide = {
        ...slide,
        content: {
          ...slide.content,
          image: {
            ...slide.content.image,
            url: imageUrl,
          },
        },
      };

      const newSlides = [...prev];
      newSlides[slideIndex] = updatedSlide;
      return newSlides;
    });
  }, []);

  // Start over
  const handleStartOver = useCallback(() => {
    setState('config');
    setTopic('');
    setOutline(null);
    setGeneratedSlides([]);
    setCurrentSlideIndex(0);
  }, []);

  // Render based on state
  if (state === 'config') {
    return (
      <div className="space-y-4">
        {/* Topic input */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            What do you want to learn?
          </label>
          <textarea
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g., Explain how photosynthesis works in plants..."
            className="w-full min-h-[80px] p-3 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:border-slate-300 transition-colors"
          />
        </div>

        {/* Duration pills */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Duration
          </label>
          <div className="flex gap-2 sm:gap-1 overflow-x-auto">
            {DURATION_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDuration(opt.value)}
                className={`px-3 py-2.5 sm:px-3 sm:py-1.5 rounded-md text-sm sm:text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${duration === opt.value
                  ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-200'
                  : 'bg-white text-slate-500 hover:bg-slate-100 ring-1 ring-slate-200'
                  }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Generate button */}
        <div className="pt-2">
          <button
            onClick={handleGenerateOutline}
            disabled={!topic.trim() || isGeneratingOutline}
            className={`flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-lg font-medium text-sm transition-all w-full sm:w-auto sm:ml-auto ${topic.trim() && !isGeneratingOutline
              ? 'bg-amber-500 text-white hover:bg-amber-600'
              : 'bg-slate-100 text-slate-400'
              }`}
          >
            {isGeneratingOutline ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <IconSparkles className="w-4 h-4" />
            )}
            Generate Outline
          </button>
        </div>
      </div>
    );
  }

  if (state === 'outline') {
    return (
      <div className="space-y-4">
        {/* Outline header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">{outline?.title}</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              {outline?.slideCount} slides • ~{outline?.estimatedDuration} min
            </p>
          </div>
          <button
            onClick={handleStartOver}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Start over
          </button>
        </div>

        {/* Outline items */}
        <div className={`space-y-1.5 transition-opacity ${isGeneratingOutline ? 'opacity-50' : ''}`}>
          {outline?.slides.map((slide, index) => (
            <div
              key={slide.id}
              className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 text-sm"
            >
              <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-600 text-xs font-medium flex items-center justify-center flex-shrink-0">
                {index + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-700 truncate">{slide.title}</p>
                <p className="text-xs text-slate-400 truncate">{slide.description}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${slide.type === 'diagram' ? 'bg-blue-100 text-blue-600' :
                slide.type === 'interactive' ? 'bg-emerald-100 text-emerald-600' :
                  slide.type === 'title' ? 'bg-purple-100 text-purple-600' :
                    slide.type === 'summary' ? 'bg-orange-100 text-orange-600' :
                      'bg-slate-100 text-slate-500'
                }`}>
                {slide.type}
              </span>
            </div>
          ))}
        </div>

        {/* Refinement input */}
        <div className="relative rounded-lg border border-slate-200 bg-white overflow-hidden focus-within:border-slate-300 transition-colors">
          <textarea
            value={refinementInput}
            onChange={(e) => setRefinementInput(e.target.value)}
            placeholder="Request changes... e.g., 'Add more detail about the Calvin cycle'"
            className="w-full min-h-[60px] p-3 pr-3 pb-10 resize-none border-0 outline-none text-sm text-slate-700 placeholder:text-slate-400 bg-transparent"
          />
          <div className="absolute bottom-2 right-2 flex gap-2">
            <button
              onClick={handleRegenerateOutline}
              disabled={!refinementInput.trim() || isGeneratingOutline}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium text-xs transition-all ${refinementInput.trim() && !isGeneratingOutline
                ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                : 'bg-slate-100 text-slate-400'
                }`}
            >
              {isGeneratingOutline ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <IconRefresh className="w-3.5 h-3.5" />
              )}
              {isGeneratingOutline ? 'Revising...' : 'Revise'}
            </button>
          </div>
        </div>

        {/* Generate slides button */}
        <div className="flex justify-end">
          <button
            onClick={handleGenerateSlides}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm bg-amber-500 text-white hover:bg-amber-600 transition-all"
          >
            <IconSparkles className="w-4 h-4" />
            Generate Slides
          </button>
        </div>
      </div>
    );
  }

  if (state === 'generating') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">{outline?.title}</h3>
          <span className="text-sm text-slate-500">
            Generating slides...
          </span>
        </div>

        {/* Progress list */}
        <div className="space-y-1.5">
          {outline?.slides.map((slide, index) => {
            const isComplete = index < generatedSlides.length;
            const isCurrent = index === currentGeneratingIndex && index >= generatedSlides.length;

            return (
              <div
                key={slide.id}
                className={`flex items-center gap-3 p-2 rounded-lg text-sm transition-all ${isComplete ? 'bg-emerald-50' : isCurrent ? 'bg-amber-50' : 'bg-slate-50'
                  }`}
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  {isComplete ? (
                    <Check className="w-4 h-4 text-emerald-500" />
                  ) : isCurrent ? (
                    <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                  ) : (
                    <Circle className="w-4 h-4 text-slate-300" />
                  )}
                </div>
                <span className={`flex-1 truncate ${isComplete ? 'text-emerald-700' : isCurrent ? 'text-amber-700' : 'text-slate-500'
                  }`}>
                  {slide.title}
                </span>
              </div>
            );
          })}
        </div>

        {/* Preview available slides */}
        {generatedSlides.length > 0 && (
          <button
            onClick={() => {
              setState('ready');
              setCurrentSlideIndex(0);
            }}
            className="w-full py-2 text-sm text-amber-600 hover:text-amber-700 transition-colors"
          >
            Preview {generatedSlides.length} available slides →
          </button>
        )}
      </div>
    );
  }

  // Ready state - show slide viewer
  const currentSlide = generatedSlides[currentSlideIndex];

  return (
    <div className="space-y-3">
      {/* Mini slide viewer */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="aspect-[16/10] relative">
          {currentSlide && (
            <SlideWithImage
              slide={currentSlide}
              isVisible={true}
              journalId={journalId}
              compact={currentSlide.layout !== 'interactive'}
              onImageResolved={handleImageResolved}
            />
          )}
        </div>
      </div>

      {/* Navigation bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevSlide}
            disabled={currentSlideIndex === 0}
            className={`p-1.5 rounded-md transition-colors ${currentSlideIndex === 0
              ? 'text-slate-300'
              : 'text-slate-500 hover:bg-slate-100'
              }`}
          >
            <IconChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-slate-500">
            {currentSlideIndex + 1} / {generatedSlides.length}
          </span>
          <button
            onClick={handleNextSlide}
            disabled={currentSlideIndex === generatedSlides.length - 1}
            className={`p-1.5 rounded-md transition-colors ${currentSlideIndex === generatedSlides.length - 1
              ? 'text-slate-300'
              : 'text-slate-500 hover:bg-slate-100'
              }`}
          >
            <IconChevronRight className="w-4 h-4" />
          </button>
          {/* Show spinner only when actively generating (not on reload with partial slides) */}
          {isGeneratingSlides && outline && generatedSlides.length < outline.slideCount && (
            <div
              className="flex items-center gap-1.5 ml-2"
              title={`Generating ${outline.slideCount - generatedSlides.length} more slide${outline.slideCount - generatedSlides.length > 1 ? 's' : ''}...`}
            >
              <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleStartOver}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            New explainer
          </button>
          {/* Show Open button when at least one slide is generated */}
          {onOpenInTab && outline && generatedSlides.length > 0 && (
            <button
              onClick={handleOpenInTab}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
            >
              <IconExternalLink className="w-3.5 h-3.5" />
              Open
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

